import React, { useContext, useEffect, useState } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, btnP, btnSec, sp, rad, type } from "../../constants/styles";

const CATEGORY_META = {
  hall:         { label: "Hall / Living Room", icon: "🛋️" },
  bedroom:      { label: "Bedroom",            icon: "🛏️" },
  kitchen:      { label: "Kitchen",            icon: "🍳" },
  bathroom:     { label: "Bathroom",           icon: "🚿" },
  walls:        { label: "Walls",              icon: "🧱" },
  meter:        { label: "Meter Reading",      icon: "📊" },
  other:        { label: "Other",              icon: "📷" },
  damage_proof: { label: "Damage Proof",       icon: "⚠️" },
};

const REASON_LABEL = {
  damage: "Property Damage",
  cleaning: "Cleaning",
  painting: "Painting",
  unpaid_bills: "Unpaid Bills",
  other: "Other",
};

const fmtPaise = (paise) => "₹" + Math.round(Number(paise || 0) / 100).toLocaleString('en-IN');

/**
 * Admin's resolution view for a disputed vacate.
 *
 * Layout mirrors VacateDetailPage (so admin and landlord see the
 * same facts) but adds:
 *   - Tenant's dispute reason prominently at the top
 *   - Read-only deduction list (admin can DELETE line items via the
 *     existing /vacate/:id/deductions/:did endpoint if they want to
 *     reduce; can add via /vacate/:id/deductions if they want to add)
 *   - Resolution note textarea
 *   - "Post Final Ruling" button → /admin-resolve transitions to
 *     'agreed' and writes admin_resolution_note. From there the
 *     landlord can settle as normal.
 */
export default function AdminVacateDetail() {
  const { h, go, selVacateId, flash, showConfirm } = useContext(Ctx);
  const [vr, setVr] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const refresh = async () => {
    if (!selVacateId) return;
    const res = await api.vacateGet(selVacateId);
    if (res?.success) {
      setVr(res.vacate_request);
      setPhotos(res.photos || []);
      setDeductions((res.deductions || []).map(d => ({
        ...d,
        proof_photo_ids: (() => { try { return JSON.parse(d.proof_photo_ids || '[]'); } catch { return []; } })(),
      })));
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await refresh(); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selVacateId]);

  if (loading) return <div style={{ paddingTop: 40, textAlign: 'center', color: h.ts }}>Loading…</div>;
  if (!vr) return <div style={{ paddingTop: 40, textAlign: 'center', color: h.ts }}>Vacate request not found</div>;

  const totalDed = deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
  const moveOutByCat = {};
  for (const p of photos) {
    if (p.uploaded_by_role !== 'tenant') continue;
    (moveOutByCat[p.category] = moveOutByCat[p.category] || []).push(p);
  }
  const damageProofs = photos.filter(p => p.uploaded_by_role === 'landlord' && p.category === 'damage_proof');

  const removeDeduction = async (did) => {
    showConfirm("Remove this deduction line as part of your ruling?", async () => {
      const res = await api.vacateDeleteDeduction(vr.id, did);
      if (res?.success) {
        setDeductions(prev => prev.filter(d => d.id !== did));
        flash("Deduction removed");
      } else {
        flash(res?.error || "Could not remove", "error");
      }
    });
  };

  const postRuling = async () => {
    if (!resolutionNote.trim()) { flash("Add a resolution note before posting your ruling.", "error"); return; }
    showConfirm(
      `Post your ruling? After this, both parties see your note and the landlord can finalise settlement. Current refund = ${fmtPaise(Math.max(0, ((vr && vr.lease_id) ? 0 : 0)))} (computed from deductions).`,
      async () => {
        setActing(true);
        try {
          const res = await api.vacateTransition(vr.id, 'admin-resolve', { resolution_note: resolutionNote.trim() });
          if (res?.success) {
            flash("Ruling posted — landlord can now finalise the settlement");
            await refresh();
          } else {
            flash(res?.message || res?.error || "Could not post ruling", "error");
          }
        } finally { setActing(false); }
      }
    );
  };

  const isDisputed = vr.status === 'disputed';

  return (
    <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("adminVacateQueue")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← Back to Dispute Queue</button>

      {/* Header card */}
      <div style={{ ...crd(h), padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ ...type.h2, color: h.tx }}>Dispute Resolution</div>
            <div style={{ color: h.tt, fontSize: 11, marginTop: 4 }}>
              Vacate ID: <span style={{ fontFamily: 'monospace' }}>{vr.id}</span>
            </div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
            padding: "4px 10px", borderRadius: 999,
            background: isDisputed ? `${h.rd}15` : `${h.gn}15`,
            color: isDisputed ? h.rd : h.gn,
          }}>{vr.status.replace(/_/g, ' ')}</span>
        </div>
      </div>

      {/* Tenant's dispute reason — prominent */}
      {vr.dispute_reason && (
        <div style={{ ...crd(h), padding: 14, marginBottom: 12, borderLeft: `4px solid ${h.rd}`, background: `${h.rd}05` }}>
          <div style={{ ...type.cap, color: h.rd, marginBottom: 6, fontWeight: 700 }}>🚩 TENANT'S DISPUTE</div>
          <div style={{ color: h.tx, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{vr.dispute_reason}</div>
          {vr.disputed_at && (
            <div style={{ fontSize: 10, color: h.tt, marginTop: 6 }}>
              Filed {new Date(vr.disputed_at).toLocaleString('en-IN')}
            </div>
          )}
        </div>
      )}

      {/* Tenant's original notes */}
      {vr.tenant_notes && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Tenant's notes</div>
          <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{vr.tenant_notes}</div>
        </div>
      )}

      {/* Landlord's notes */}
      {vr.landlord_notes && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Landlord's notes</div>
          <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{vr.landlord_notes}</div>
        </div>
      )}

      {/* Photos by category — tenant evidence */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Tenant's move-out photos</div>
        {['hall','bedroom','kitchen','bathroom','walls','meter','other'].map(cat => {
          const list = moveOutByCat[cat] || [];
          if (list.length === 0) return null;
          const meta = CATEGORY_META[cat] || { label: cat, icon: "📷" };
          return (
            <div key={cat} style={{ ...crd(h), padding: 10, marginBottom: 6 }}>
              <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: 6 }}>
                <span style={{ marginRight: 6 }}>{meta.icon}</span>{meta.label} ({list.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
                {list.map(p => (
                  <img key={p.id} src={p.url} alt={cat} loading="lazy" onClick={() => setLightboxUrl(p.url)}
                    style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Deductions under dispute */}
      <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: 8 }}>
          💸 Landlord's deductions — total {fmtPaise(totalDed)}
        </div>
        {deductions.length === 0 ? (
          <div style={{ color: h.tt, fontSize: 11, fontStyle: 'italic' }}>No deductions on file.</div>
        ) : (
          deductions.map(d => {
            const proofs = (d.proof_photo_ids || []).map(pid => damageProofs.find(p => p.id === pid)).filter(Boolean);
            return (
              <div key={d.id} style={{
                padding: 10, borderRadius: rad.sm,
                border: `1px solid ${h.bd}`, marginBottom: 6,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>{REASON_LABEL[d.reason] || d.reason}</div>
                    {d.description && <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{d.description}</div>}
                    {proofs.length > 0 && (
                      <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                        {proofs.map(p => <img key={p.id} src={p.url} alt="" onClick={() => setLightboxUrl(p.url)} style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${h.bd}` }} />)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ ...type.sm, color: h.rd, fontWeight: 700 }}>-{fmtPaise(d.amount)}</div>
                    {isDisputed && (
                      <button onClick={() => removeDeduction(d.id)} style={{ background: "none", border: "none", color: h.rd, cursor: "pointer", fontSize: 10, marginTop: 4 }}>Strike out</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {isDisputed && (
          <div style={{ marginTop: 8, padding: 8, background: `${h.wn}08`, border: `1px solid ${h.wn}25`, borderRadius: rad.sm, fontSize: 10, color: h.wn, lineHeight: 1.5 }}>
            ℹ️ You can strike out any deduction you find unfair before posting your ruling. The remaining
            deductions will be the final amount.
          </div>
        )}
      </div>

      {/* Resolution composer */}
      {isDisputed && (
        <div style={{ ...crd(h), padding: 14, marginBottom: 12, border: `2px solid ${h.ac}30` }}>
          <div style={{ ...type.cap, color: h.ac, marginBottom: 8, fontWeight: 700 }}>⚖️ Your Ruling</div>
          <textarea
            value={resolutionNote}
            onChange={e => setResolutionNote(e.target.value)}
            placeholder="Summarise your decision and the reasoning. Both tenant and landlord will see this verbatim. Be concrete — what stays, what's removed, and why."
            rows={5}
            maxLength={2000}
            style={{
              width: "100%", padding: 10, borderRadius: rad.sm,
              border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
              fontSize: 12, fontFamily: "inherit", resize: "vertical",
              boxSizing: "border-box", marginBottom: 8,
            }}
          />
          <button onClick={postRuling} disabled={acting || !resolutionNote.trim()} style={{
            ...btnP(h), width: "100%", padding: 12,
            opacity: (acting || !resolutionNote.trim()) ? 0.55 : 1,
            cursor: (acting || !resolutionNote.trim()) ? "not-allowed" : "pointer",
          }}>{acting ? "Posting…" : "⚖️ Post Final Ruling"}</button>
        </div>
      )}

      {/* Already resolved — show the ruling */}
      {!isDisputed && vr.admin_resolution_note && (
        <div style={{ ...crd(h), padding: 14, marginBottom: 12, background: `${h.gn}05`, borderLeft: `4px solid ${h.gn}` }}>
          <div style={{ ...type.cap, color: h.gn, marginBottom: 6, fontWeight: 700 }}>✓ ADMIN RULING POSTED</div>
          <div style={{ color: h.tx, fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{vr.admin_resolution_note}</div>
          {vr.agreed_at && (
            <div style={{ fontSize: 10, color: h.tt, marginTop: 6 }}>
              Resolved {new Date(vr.agreed_at).toLocaleString('en-IN')} · Status: {vr.status.replace(/_/g,' ')}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <img src={lightboxUrl} alt="" style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
